import { Collections } from '@freelancer/datastore/collections.model';
import * as Rx from 'rxjs';
import { bufferTime, filter, map } from 'rxjs/operators';
import { RequestDataPayload } from '../../actions';
import { Ordering } from '../../query';
import { RawQuery } from '../../store.model';

/**
 * Batches requests that occur within `windowTime` milliseconds of each other,
 * such that at the end of the interval, requests to the same part of the
 * datastore are grouped together into one request. Requests will not be batched
 * if they differ in either collection or authUid.
 *
 * Example: With a window size of 3 seconds, a request for user with id 1 (U1),
 * then for ids 2 and 3 (U2:3) one second later, then for project 5 (P5)
 *
 * 0     1     2     3     4     5s
 * U1----U2:3----P5--------------|
 *        batchRequests(3000)
 * ------------------B-----------|
 *
 * Where B is the list [ U1:2:3, P5 ]
 */
export function batchRequests(
  windowTime: number,
  scheduler: Rx.Scheduler = Rx.asyncScheduler,
): (
  source: Rx.Observable<RequestDataPayload<any>>,
) => Rx.Observable<ReadonlyArray<RequestDataPayload<any>>> {
  if (windowTime === 0) {
    return (source: Rx.Observable<RequestDataPayload<any>>) =>
      source.pipe(map(request => [request]));
  }

  return (requestStream: Rx.Observable<RequestDataPayload<any>>) =>
    requestStream.pipe(
      bufferTime(windowTime, scheduler),
      filter(requests => requests.length > 0),
      map(requests => {
        const requestsToBatch = requests.reduce(
          (toBatch, req) => {
            const key = `${req.type}-${req.ref.path.authUid}`;
            const ids = req.ref.path.ids || [];
            let existingIds: ReadonlyArray<string> = [];

            // TODO: Add batch limits to prevent overloading backend
            if (toBatch[key]) {
              existingIds = toBatch[key].ref.path.ids || [];
              toBatch[key].ref.path.ids = [...existingIds, ...ids];
              // A batch request id is the concatanation of the orginal request ids
              toBatch[key].clientRequestIds = [
                ...toBatch[key].clientRequestIds,
                ...req.clientRequestIds,
              ];
              return toBatch;
            }

            return {
              ...toBatch,
              [key]: {
                type: req.ref.path.collection,
                ref: {
                  path: {
                    collection: req.ref.path.collection,
                    authUid: req.ref.path.authUid,
                    ids,
                  },
                },
                clientRequestIds: req.clientRequestIds,
              },
            };
          },
          {} as { [key: string]: MutableRequestDataPayload<any> },
        );

        return Object.values(requestsToBatch);
      }),
    );
}

interface MutableRequestDataPayload<K extends keyof Collections> {
  readonly type: K; // While this is in the ref, this helps TypeScript do the discriminated union
  readonly ref: MutableReference<K>;
  // tslint:disable-next-line:readonly-keyword
  clientRequestIds: ReadonlyArray<string>;
}

interface MutableReference<K extends keyof Collections> {
  readonly path: MutablePath<K>;
  readonly query?: RawQuery<Collections[K]>;
  readonly order?: Ordering<K>;
}

interface MutablePath<K extends keyof Collections> {
  readonly collection: K;
  readonly authUid: string;
  // tslint:disable-next-line:readonly-keyword
  ids?: ReadonlyArray<string>;
}
